import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { validateToken } from '../api/authAPI';
import { getServerSession, loadServerSession } from '../api/sessionAPI';
import { refreshSession, signOut } from '../authService';
import { useInactivityTimer } from '../hooks/useInactivityTimer';
import { User } from '../types/user';
import { handleAuthError, clearAuthTokens } from '../utils/authErrorHandler';
import { getMsUntilExpiry } from '../utils/tokenUtils';
import { mapWhoAmIResponseToUser } from '../utils/userUtils';
import { getViewAsToken, isViewAs } from '../viewAs/viewAsSession';

/** HA-447: a view-as tab has its session token; HA-445: otherwise the API's session. */
const hasSessionToken = (): boolean =>
  isViewAs() ? getViewAsToken() !== null : getServerSession() !== null;

export type SessionState = 'active' | 'warning' | 'expired';

/** Appears in SessionExpiryModal and tests. 2 minutes before expiry. */
export const SESSION_WARNING_WINDOW_MS = 120_000;
/** Idle threshold — idle longer than this during the warning window triggers the modal. */
export const SESSION_IDLE_THRESHOLD_MS = 60_000;
/** How often the watchdog checks expiry. */
export const SESSION_CHECK_INTERVAL_MS = 30_000;
/**
 * HA-445: only an explicit refresh counts as activity on the API (its 15-minute idle limit), so
 * the page tells it on the user's first input once this long has passed since the last refresh.
 * The session then ends within 14-15 minutes of the last input, never later.
 */
export const SESSION_ACTIVE_REFRESH_MS = 60_000;

/** Input that counts as the user being here. */
const SESSION_ACTIVITY_EVENTS: Array<keyof WindowEventMap> = [
  'keydown',
  'pointerdown',
  'mousemove',
  'wheel',
  'touchstart',
  'scroll',
];

interface AuthContextType {
  currentUser: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  sessionState: SessionState;
  refreshAuth: () => Promise<void>;
  logout: () => Promise<void>;
  extendSession: () => Promise<boolean>;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [sessionState, setSessionState] = useState<SessionState>('active');

  const { getIdleMs, markActive } = useInactivityTimer();

  // HA-445: the session watchdog and the activity refresh run while the page has a session: from
  // the API's session at load (even if whoami then fails) and once signed in, without restarting
  // in between. Never in a view-as tab (HA-447).
  const watching = !isViewAs() && (isAuthenticated || getServerSession() !== null);
  const refreshInFlightRef = useRef<Promise<boolean> | null>(null);

  useEffect(() => {
    const checkAuthState = async () => {
      setIsLoading(true);
      try {
        if (hasSessionToken()) {
          const userData = await validateToken();
          const user: User = mapWhoAmIResponseToUser(userData);

          setCurrentUser(user);
          setIsAuthenticated(true);
          setSessionState('active');
        }
      } catch (error) {
        console.error('Authentication state check failed:', error);
        setCurrentUser(null);
        setIsAuthenticated(false);
      } finally {
        setIsLoading(false);
      }
    };

    checkAuthState();
  }, []);

  const refreshAuth = useCallback(async (): Promise<void> => {
    setIsLoading(true);
    try {
      if (hasSessionToken()) {
        const userData = await validateToken();
        const user: User = mapWhoAmIResponseToUser(userData);

        setCurrentUser(user);
        setIsAuthenticated(true);
        setSessionState('active');
      } else {
        setCurrentUser(null);
        setIsAuthenticated(false);
      }
    } catch (error) {
      console.error('Authentication refresh failed:', error);
      setCurrentUser(null);
      setIsAuthenticated(false);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const logout = useCallback(async (): Promise<void> => {
    try {
      setIsLoading(true);
      // HA-445: the API ends the session and clears its cookie. HA-447: a view-as tab ends its
      // support session instead.
      if (isViewAs()) {
        clearAuthTokens();
      } else {
        await signOut();
      }
      setCurrentUser(null);
      setIsAuthenticated(false);
      setSessionState('active');
    } catch (error) {
      console.error('Logout failed:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Shared refresh path — ensures concurrent callers (the watchdog tick
   * racing with the user clicking "Stay signed in") collapse to a single
   * POST /auth/refresh.
   */
  const extendSession = useCallback(async (): Promise<boolean> => {
    if (refreshInFlightRef.current) {
      return refreshInFlightRef.current;
    }
    const promise = refreshSession()
      .then((ok) => {
        if (ok) {
          setSessionState('active');
          markActive();
        }

        return ok;
      })
      .finally(() => {
        refreshInFlightRef.current = null;
      });

    refreshInFlightRef.current = promise;

    return promise;
  }, [markActive]);

  // HA-445: tell the API the user is here on the leading edge of their activity: the first input
  // once SESSION_ACTIVE_REFRESH_MS has passed since the last refresh. Not in a view-as tab (its
  // cookie is the admin's), and not near the absolute limit, which nothing extends.
  useEffect(() => {
    if (!watching) return;

    let lastAttemptAt = 0;

    const onActivity = (event: Event) => {
      // Chrome fires mousemove when content moves under a still pointer; that isn't the user.
      if (
        event.type === 'mousemove' &&
        (event as MouseEvent).movementX === 0 &&
        (event as MouseEvent).movementY === 0
      ) {
        return;
      }
      const session = getServerSession();

      if (
        !session ||
        session.idleExpiresAt >= session.absoluteExpiresAt ||
        Date.now() - session.refreshedAt < SESSION_ACTIVE_REFRESH_MS ||
        // A failed attempt doesn't move refreshedAt: don't retry on every input (offline, say).
        Date.now() - lastAttemptAt < SESSION_ACTIVE_REFRESH_MS
      ) {
        return;
      }
      lastAttemptAt = Date.now();
      void extendSession().then((ok) => {
        // The API refused: the session has ended (idle, absolute, revoked or signed out).
        if (!ok && !getServerSession()) {
          setSessionState('expired');
          handleAuthError('session_expired');
        }
      });
    };

    SESSION_ACTIVITY_EVENTS.forEach((name) =>
      window.addEventListener(name, onActivity, { passive: true })
    );

    return () => {
      SESSION_ACTIVITY_EVENTS.forEach((name) => window.removeEventListener(name, onActivity));
    };
  }, [watching, extendSession]);

  // Session watchdog — polls the session's expiry while authenticated.
  // HA-447: off in a view-as tab. Its session can't be extended; the banner counts it down and
  // ends it, and a refresh here would extend the admin's own session.
  useEffect(() => {
    if (!watching) return;

    let cancelled = false;
    let consecutiveFailures = 0;

    const tick = async () => {
      // Once three in a row fail, assume something is broken (clock skew,
      // revoked refresh token, backend key rotation) and stop the watchdog
      // instead of hammering every 30s.
      if (consecutiveFailures >= 3) {
        if (!cancelled) {
          setSessionState('expired');
          handleAuthError('session_expired');
        }

        return;
      }

      let msUntilExpiry = getMsUntilExpiry();

      // The session is gone (a refused refresh, or a 401 on a re-read): the page is signed out.
      if (msUntilExpiry == null) {
        if (!cancelled) {
          setSessionState('expired');
          handleAuthError('session_expired');
        }

        return;
      }

      // HA-445: another tab may have kept the session alive, or ended it. Re-read it before
      // warning or expiring (reading isn't activity, so it extends nothing).
      if (msUntilExpiry <= SESSION_WARNING_WINDOW_MS) {
        await loadServerSession();
        if (cancelled) return;
        msUntilExpiry = getMsUntilExpiry();

        if (msUntilExpiry == null || msUntilExpiry <= 0) {
          setSessionState('expired');
          handleAuthError('session_expired');

          return;
        }
      }

      // An active user near the end gets a refresh rather than the prompt (the activity listener
      // above normally gets there first); near the absolute limit there is nothing to extend.
      const session = getServerSession();
      const canExtend = session !== null && session.idleExpiresAt < session.absoluteExpiresAt;
      const nearExpiry = msUntilExpiry <= SESSION_WARNING_WINDOW_MS;
      const activeNearExpiry = nearExpiry && getIdleMs() <= SESSION_IDLE_THRESHOLD_MS;

      if (canExtend && activeNearExpiry) {
        const ok = await extendSession();

        if (ok) {
          consecutiveFailures = 0;
        } else if (!getServerSession()) {
          // The API refused: the session has ended (idle, absolute, revoked or signed out).
          if (!cancelled) {
            setSessionState('expired');
            handleAuthError('session_expired');
          }
        } else {
          consecutiveFailures += 1;
        }

        return;
      }

      if (nearExpiry) {
        // Idle (or the absolute limit is near): ask before it ends.
        if (!cancelled) setSessionState('warning');
      } else {
        // Well inside valid window — reset the failure counter.
        consecutiveFailures = 0;
      }
    };

    void tick();
    const id = window.setInterval(() => void tick(), SESSION_CHECK_INTERVAL_MS);

    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [watching, extendSession, getIdleMs]);

  const contextValue = useMemo(
    () => ({
      currentUser,
      isAuthenticated,
      isLoading,
      sessionState,
      refreshAuth,
      logout,
      extendSession,
    }),
    [currentUser, isAuthenticated, isLoading, sessionState, refreshAuth, logout, extendSession]
  );

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);

  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }

  return context;
};
